<script>

	// NB: To close already-open avatar menus when a new one is opened, we use the technique here:
	// https://www.arzidava.com/blog/svelte-functions-on-children/

	import { onMount, onDestroy, tick, createEventDispatcher } from 'svelte';
	import { crossfade, fly, slide, fade } from 'svelte/transition';
	import { flip } from 'svelte/animate';
	import { quintOut } from 'svelte/easing';

	import moment from 'moment-timezone';
	import { byValue, byNumber } from 'sort-es';

	import Question from "./QA/Question.svelte";

	import Button from "../../../ui/Button.svelte";
	import Switch from "../../../ui/fields/Switch.svelte";
	import Menu from "../../../ui/Menu.svelte";
	import Slate from "../../../ui/Slate.svelte";

	import {	event, bigError, attendee, attendeeLikes, syncClient, session, showSettings, settings } from '../../../lib/stores.js';
	import { text_to_html } from '../../../lib/html.js';
	import { postServerData } from '../../../lib/prelude.js';
	import { randomIntFromInterval } from '../../../lib/utils.js';
	import {	autoblur } from '../../../lib/autoblur.js';
	import { db } from "../../../lib/db.js";
	// import { logInteraction } from '../../../lib/usage.js';

	const dispatch = createEventDispatcher();

	const utilsThreshold = 0;

	const [send, receive] = crossfade({
		duration: 0,
		fallback(node, params) {
			return {
				duration: 300,
				easing: quintOut,
				css: t => `
					transform: scale(${1 - ((1 - t) / 32)});
					opacity: ${t}
				`
			};
		}
	});

	// let mod = ($attendee.type == 'login') || ($backstage.includes($session.ref));
	// let mod = ($attendee.type == 'login');

	// showSort is sort-only; showUtils is filter+sort
	let mod = false;
	let showUtils = false;
	let showSort = false;

	$: mod = ($settings && $settings.room && (($settings.mode == 'presenter') || ($settings.mode == 'av'))) ? false : true;
	$: showUtils = (mod && $event.setup.app.questionModeration) ? true : false;
	$: showSort = ($settings && $settings.room && ($settings.mode == 'presenter')) ? true : false;

	let mounted = false;

	let channel;

	let questions = [];
	let displayQuestions = [];
	let bound = {};

	let approvedCount = 0;

	let filter = 'all';
	let sort = 'latest';

	let filterMenu = false;
	let sortMenu = false;

	let flipDuration = 400;

	// let selectedMsg = null;

	let remaining = 400;
	let messageText = '';
	let anon = false;

	let ta;

	let rowTimeout;

	let ready = false;

	let participants = {};

	const menuLabels = {
		'all': 'Current questions',
		'pending': 'New questions',
		'approved': 'Approved questions',
		'rejected': 'Rejected questions',
		'closed': 'Closed questions',
		'latest': 'Latest',
		'likes': 'Most liked'
	};

	let filterOptions = [
		{ ref: 'all', label: menuLabels['all'] },
		{ ref: 'pending', label: menuLabels['pending'] },
		{ ref: 'approved', label: menuLabels['approved'] },
		{ ref: 'rejected', label: menuLabels['rejected'] },
		{ ref: 'closed', label: menuLabels['closed'] }
	];

	let fullscreenSend = false;

	let mastheadHeight = 0;

	onMount(async () => {

		mastheadHeight = document.getElementById('masthead').offsetHeight;

		try {

			// console.log("opening session channel (Q+A)");
			channel = await $syncClient.list($session.syncChannel);

			channel.on('itemAdded', function(i) {
				if (i.item.data.type == 'qa') {
					addQuestion(i.item);
				}
			});

			channel.on('itemUpdated', function(i) {
				if (i.item.data.type == 'qa') {
					updateQuestion(i.item);
				}
			});

			channel.on('itemRemoved', function(i) {
				if (i.previousItemData.type == 'qa') {
					deleteQuestion(i.index);
				}
			});

			await getQA();
			
		} catch (e) {

			console.log("Sync error", e, $session.syncChannel);
			
		}

		let delay = questions.length ? 500 : 0;

		setTimeout(() => {
			ready = true;
		}, delay);

		mounted = true;

	});

	function qaPageHandler(paginator) {
		if (paginator.items && paginator.items.length) {
			paginator.items.forEach(function(item) {
				if (item.data.type == 'qa') {
					addQuestion(item);
				}
			});
			return paginator.hasNextPage ? paginator.nextPage().then(qaPageHandler) : null;
		}
	};

	async function getQA() {
		let paginator;
		try {
			paginator = await channel.getItems({
				from: 0,
				order: 'asc',
				pageSize: 100
			});
		} catch (error) {
			if (error.message.match('Maximum attempt')) {
				// back off and try again later...
				const smear = 1000 * randomIntFromInterval(5,10);
				setTimeout(async () => {
					await getQA();
				}, smear);
			} else {
				console.error('getQA failed', error);
			}
		}
		if (paginator) await qaPageHandler(paginator);
	}

	async function addQuestion(item) {
		// console.log('addQuestion', item);
		let question = processQuestion(item);
		if (question.sender) {
			if (!participants[question.sender]) participants[question.sender] = await db.attendees.get(question.sender);
		}
		question.index = item.index;
		questions.unshift(question);
		questions = questions;
	}

	async function updateQuestion(item) {
		// console.log('updateQuestion', item.data);
		const idx = item.index;
		for (let [i,r] of questions.entries()) {
			if (r.index == idx) {
				let question = processQuestion(item);
				question.index = r.index;
				questions[i] = question;
				break;
			}
		}
		questions = questions;
	}

	function deleteQuestion(idx) {
		// console.log('deleteQuestion', idx);
		questions = questions.filter(m => {
			if (m.index != idx) return m;
		});
	}

	function processQuestion(item) {
		let html = text_to_html(item.data.message);
		let sent = item.data.approved ? moment.unix(item.data.approved) : moment.unix(item.data.dt);
		let q = {
			revision: item.index + '/' + item.revision,
			index: item.index,
			message: html,
			sent: sent,
			sender: item.data.sender,
			likes: item.data.likes ? item.data.likes : 0,
			liked: $attendeeLikes.includes($session.syncChannel + '/' + item.index) ? true : false,
			approved: item.data.approved ? true : false,
			rejected: item.data.rejected ? true : false,
			closed: item.data.closed ? true : false,
			anon: item.data.anon ? true : false,			
		};
		return q;
	}

	// console.log('backstage?', $session.ref, $backstage);

	async function sendQuestion() {

		fullscreenSend = false;

		if ((messageText != '') && (remaining >= 0)) {
			// logInteraction('now/qa');
			await postServerData('onstage/qa', {
				channelRef: $session.syncChannel,
				message: messageText,
				anon: anon
			});
			if (!$bigError) {
				messageText = '';
				await tick();
				resize({ target: ta });
				// ta.focus();
				ta.blur();
			}
		}

	}

	function checkMessage(e) {
		if (e.code == 'Enter') {
			e.target.form.dispatchEvent(new Event("submit", {cancelable: true}));
			e.preventDefault();
		}
	}

	function resize({ target }) {
		target.style.height = "1px";
		target.style.height = (+target.scrollHeight)+"px";
	}

	function autoresize(el) {
		resize({ target: el });
		el.style.overflow = 'hidden';
		el.addEventListener('input', resize);

		return {
			destroy: () => el.removeEventListener('input', resize)
		}
	}

	function setDisplayQuestions() {
		clearTimeout(rowTimeout);
		rowTimeout = setTimeout(function(){
			if (sort == 'likes') {
				displayQuestions = questions.sort(byValue(a => a.likes, byNumber({ desc: true })));
			} else {
				displayQuestions = questions.sort(byValue(a => a.index, byNumber({ desc: true })));
			}
			displayQuestions = (displayQuestions && displayQuestions.length) ? displayQuestions.filter(filterByValue) : [];
			// console.log(questions);
			approvedCount = questions.filter(q => q.approved).length;
			// console.log('setDisplayQuestions', sort, filter, approvedCount);
		}, 100);
	}

	function filterByValue(q) {
		if (!q.approved && !mod && !(q.sender == $attendee.ref)) return false;
		if (filter == 'pending') {
			return !q.approved && !q.rejected && !q.closed;
		} else if (filter == 'approved') {
			return q.approved && !q.closed;
		} else if (filter == 'rejected') {
			return q.rejected;
		} else if (filter == 'closed') {
			return q.closed;
		} else {
			return !q.rejected && !q.closed;
		}
	}

	function setFilter(e) {
		filter = e.detail.ref;
		filterMenu = false;
		flipDuration = 200;
		setTimeout(() => {
			flipDuration = 400;
		}, 400);
	}

	function setSort(e) {
		sort = e.detail.ref;
		sortMenu = false;
		flipDuration = 200;
		setTimeout(() => {
			flipDuration = 400;
		}, 400);
	}

	function closeMenus(e) {
		let except = e.detail;
		for (const [k,v] of Object.entries(bound)) {
			if (k != except) {
				v.closeMenu();
			}
		}
	}

	function closeSortMenu() {
		sortMenu = false;
	}

	function closeFilterMenu() {
		filterMenu = false;
	}

	async function makeFullscreen(e) {
		if (!fullscreenSend) {
			e.preventDefault();
			fullscreenSend = true;
			await tick();
			ta.focus();
		}
	}

	$: remaining = 400 - messageText.length;

	$: if (questions || sort || filter) {
		setDisplayQuestions();
	}

	$: if (filterMenu) {
		closeSortMenu();
	}

	$: if (sortMenu) {
		closeFilterMenu();
	}

	onDestroy(() => {
		// console.log("closing session channel (Q+A)");
		if (channel) channel.close();
	});

</script>

<style>
	.wrap {
		position: absolute;
		inset:  0;
		display: flex;
		flex-direction: column;
	}
	.qa {
		flex: 1 1 auto;
		overflow-y: auto;
		/*padding: 0 0.5rem 1rem 0.5rem;*/
		padding: 0 0.5rem 2rem 0.5rem;
		position: relative;
		opacity: 0;
		transition: opacity 0.2s ease-in;
	}

	@media (max-width: 955px) {
		.qa {
			padding: 0 0.5rem 7rem 0.5rem;
		}
	}

	.qa.ready {
		opacity: 1;
	}

	.qa > div {
		margin-top: 0.5rem;
	}


	.utils {
		position: sticky;
		top: 0;
		margin: 0 0.4rem;
		padding: 0 0 0.3rem 0;
		/*border-bottom: 1px solid var(--blend-80, #888);*/
		background: var(--bgColor);
		color: var(--textColor);
		z-index: 5000;
		box-sizing: border-box;
		display: flex;
		justify-content: space-between;
	}

	.util {
		display: flex;
		gap: 0.2rem;
	}
	.util span {
		position: relative;
		z-index: 5000;
		display: block;
		margin: 0;
		padding: 0.25rem 1.2em 0.2rem 0;
		font-weight: 400;
		font-size: 0.9rem;
	}
	.util svg {
		display: inline-block;
/*		width: 0.5em;*/
		width: 0.4em;
		transform: rotate(90deg);
		position: relative;
		left: -0.25rem;
		top: 0.05rem;
		transition: transform 0.2s ease;
	}
	.util.open svg {
		transform: rotate(-90deg);
	}
	.util:hover {
		cursor: pointer;
		color: var(--accentColor, #e6007e);
	}

	.util :global(.menu) {
		position: absolute;
		top: calc(100% - 2px);
		left: 0;
		width: 100%;
	}

	.send {
		flex: 0 0 auto;
	}
	.send > form {
		margin: 0 1;
		border-top: 1px solid var(--blend-20);
		padding: 0.5rem 0;
		font-size: 0.8rem;
	}
	.send > form > div {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.send.fullscreen > form > div {
		width: 100%;
	}
	.send label {
		font-weight: 600;
		line-height: 1;
	}
	.counter {
		display: none;
		font-size: 0.8rem;
		line-height: 1;
		color: var(--blend-60);
	}
	.counter.over {
		color: var(--errorColor);
	}
	.send textarea {
		/*border: 1px solid var(--blend-20);
		padding: 0.5rem;
		border-radius: 3px;*/
		border: 0;
		padding: 0;
		width: 100%;
		min-height: 3.2rem;
		max-height: 15rem;
		background: none;
		resize: none;
		font-family: Inter;
		font-size: 1rem;
		color: var(--textColor);
		text-align: left;
	}
	.send textarea:focus {
		outline: none;
		border-color: var(--blend-60);
	}
	.send :global(button) {
		margin-left: auto;
		padding: 0.1rem 1rem;
	}
	.send .switch {
		display: flex;
		gap: 0.5rem;
		align-items: center;
	}
	.send .switch label {
		font-weight: 400;
		color: var(--blend-60);
	}

	::-webkit-input-placeholder { /* Chrome/Opera/Safari */
	  color: var(--blend-60);
	}
	::-moz-placeholder { /* Firefox 19+ */
	  color: var(--blend-60);
	}
	:-ms-input-placeholder { /* IE 10+ */
	  color: var(--blend-60);
	}
	:-moz-placeholder { /* Firefox 18- */
	  color: var(--blend-60);
	}

	.send {
		transition: transform 0.4s ease;
		transform: translateY(10px);
		position: relative;
		top: -10px;
	}

	.send.fullscreen {
		position: fixed;
		top: auto;
		left: 0;
		width: 100%;
/*		height: 88dvh;*/
		height: calc(100dvh - calc(var(--mh) + 5px));
		bottom: 0;
		background: var(--bgColor);
		z-index: 9000;
		padding-top: 4rem;
		box-shadow: 0 0 10px var(--shadow);
		border-top-left-radius: 16px;
		border-top-right-radius: 16px;
		box-shadow: 0 0 2rem var(--shadow, #000);
		border: 1px solid var(--blend-10, #eee);
		border-bottom: none;
		transform: translateY(0);
		box-sizing: border-box;
	}

	@media all and (min-width: 800px) {
		.send.fullscreen {
			max-width: 800px;
			left: calc(50vw - 400px);
		}
	}

	.send > form {
		margin: 0 1rem;
		border-top: 1px solid var(--blend-20);
/*		padding: 0.5rem 0 0.5rem 0.5rem;*/
		padding: 0.5rem 0;
		display: flex;
		align-items: flex-start;
		position: relative;
		gap: 1rem;
	}

	.send.fullscreen > form {
		flex-direction: column;
		border: none;
		padding-top: 0;
	}

	/*.send.fullscreen > :global(button.subtle) {
		position: absolute;
		top: 0.5rem;
		left: 0.5rem;
		width: 2rem;
		height: 2rem;
		color: var(--textColor);
		padding: 0;
	}

	.send.fullscreen > :global(button.subtle svg) {
		inset: unset;
		width: 2rem;
		height: 2rem;
	}*/

	.send button {
		flex: 0 0 auto;
		margin-left: auto;
		padding: 0;
		border: 0;
		background: transparent;
		width: 2rem;
		height: 2rem;
		position: relative;
		box-sizing: border-box;
		border-radius: 3px;
		color: var(--bgColor);
		background: var(--accentColor);
	}
	.send button > svg {
		position: absolute;
		top: 0.15rem;
		left: 0.15rem;
		width: calc(100% - 0.3rem);
		height: calc(100% - 0.3rem);
	}

	.send.fullscreen .counter {
		display: block;
		position: absolute;
		bottom: auto;
		top: -2.4rem;
		right: auto;
		left: 0;
		text-align: left;
	}

	.new {
		position: fixed;
		bottom: calc(2rem + env(safe-area-inset-bottom));
		right: 2rem;
	}
	.new :global(button) {
		width: 3.6rem !important;
		height: 3.6rem !important;
		box-shadow: 0 4px 8px var(--darkShadow);
	}
	.new :global(button > svg) {
		width: 2.8rem !important;
		height: 2.8rem !important;
	}

</style>

{#if mounted}
	<div class="wrap" in:fly={{ duration: 300, y: 20 }}>
		<div class="qa" class:ready>

			{#if showUtils && ((mod && questions.length > utilsThreshold) || (approvedCount.length > utilsThreshold))}
				<div class="utils" transition:slide|local={{ duration: 400, delay: 400 }}>
					<!-- TODO: nest a button instead -->
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<div
						class="util"
						class:open={filterMenu}
						on:click|stopPropagation={() => { filterMenu = !filterMenu }}
					>
						<span>{menuLabels[filter]}</span>
						<svg viewBox="0 0 16.7 29.5"><path d="M2 29.5c.5 0 1-.2 1.4-.6l12.7-12.7c.4-.4.6-.9.6-1.4s-.2-1-.6-1.4l-12.7-12.8c-.8-.8-2-.8-2.8 0-.8.8-.8 2 0 2.8l11.3 11.3-11.3 11.3c-.8.8-.8 2 0 2.8.4.5.9.7 1.4.7z"/></svg>
						{#if filterMenu}
							<Menu
								options={filterOptions}
								on:selected={setFilter}
								on:escape={closeFilterMenu}
								arrow={false}
								tick={filter}
							/>
						{/if}
					</div>
					<!-- TODO: nest a button instead -->
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<div class="util" class:open={sortMenu} on:click|stopPropagation={() => { sortMenu = !sortMenu }}>
						<span>{menuLabels[sort]}</span>
						<svg viewBox="0 0 16.7 29.5"><path d="M2 29.5c.5 0 1-.2 1.4-.6l12.7-12.7c.4-.4.6-.9.6-1.4s-.2-1-.6-1.4l-12.7-12.8c-.8-.8-2-.8-2.8 0-.8.8-.8 2 0 2.8l11.3 11.3-11.3 11.3c-.8.8-.8 2 0 2.8.4.5.9.7 1.4.7z"/></svg>
						{#if sortMenu}
							<Menu
								options={[
									{ ref: 'latest', label: menuLabels['latest'] },
									{ ref: 'likes', label: menuLabels['likes'] },
								]}
								on:selected={setSort}
								on:escape={closeSortMenu}
								arrow={false}
								tick={sort}
							/>
						{/if}
					</div>
				</div>
			{:else if showSort && (displayQuestions.length > utilsThreshold)}
				<div class="utils" transition:slide|local={{ duration: 400, delay: 400 }}>
					<div></div>
					<!-- TODO: nest a button instead -->
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<div class="util" class:open={sortMenu} on:click|stopPropagation={() => { sortMenu = !sortMenu }}>
						<span>{menuLabels[sort]}</span>
						<svg viewBox="0 0 16.7 29.5"><path d="M2 29.5c.5 0 1-.2 1.4-.6l12.7-12.7c.4-.4.6-.9.6-1.4s-.2-1-.6-1.4l-12.7-12.8c-.8-.8-2-.8-2.8 0-.8.8-.8 2 0 2.8l11.3 11.3-11.3 11.3c-.8.8-.8 2 0 2.8.4.5.9.7 1.4.7z"/></svg>
						{#if sortMenu}
							<Menu
								options={[
									{ ref: 'latest', label: menuLabels['latest'] },
									{ ref: 'likes', label: menuLabels['likes'] },
								]}
								on:selected={setSort}
								on:escape={closeSortMenu}
								arrow={false}
								tick={sort}
							/>
						{/if}
					</div>
				</div>
			{/if}

			{#each displayQuestions as q (q.index)}
				<div
					in:receive|local="{{ key: q.index }}"
					out:send|local="{{ key: q.index }}"
					animate:flip="{{ duration: flipDuration }}"
				>
					<Question
						bind:this={bound[q.index]}
						bind:q
						bind:questions
						{mod}
						{showUtils}
						{participants}
						on:highlight
						on:openMenu={closeMenus}
					/>
				</div>
			{:else}
				{#if (filter == 'all')}
					<Slate>
						<p class="icon"><svg viewBox="0 0 32 32"><path d="M18.55 21.92l-9.65 6.21v-6.21l-5.75-.01c-1.74.01-3.15-1.45-3.15-3.24v-13.42c0-1.79 1.41-3.25 3.15-3.25h21.78c1.7 0 3.07 1.41 3.07 3.15v13.52c0 1.79-1.41 3.25-3.15 3.25h-6.3zm6.16-2c.84 0 1.29-.62 1.29-1.38v-13.25c0-.71-.54-1.29-1.2-1.29h-21.51c-.71 0-1.29.62-1.29 1.38v13.16c0 .76.58 1.38 1.15 1.38l7.75.01v4.55l7.06-4.55 6.75-.01zM12.94 14.14c.01-1.79.52-2.34 1.4-2.89.6-.38 1.06-.86 1.06-1.55 0-.78-.61-1.29-1.37-1.29-.7 0-1.38.45-1.42 1.38h-1.77c.05-1.89 1.46-2.85 3.2-2.85 1.9 0 3.21 1.05 3.21 2.74 0 1.14-.57 1.89-1.49 2.43-.81.5-1.15.98-1.16 2.03v.13h-1.66v-.13zm-.23 2.14c0-.6.49-1.08 1.09-1.08.59 0 1.09.49 1.09 1.08 0 .61-.51 1.09-1.09 1.09-.6 0-1.09-.48-1.09-1.09z"/></svg></p>
						<p class="label"><strong>No questions yet.</strong></p>
						<p class="description">Be the first to ask a question.</p>
					</Slate>
				{/if}
			{/each}
		</div>

		{#if fullscreenSend}
			<div class="send" class:fullscreen={fullscreenSend} style="--mh:{mastheadHeight}px">
				{#if fullscreenSend}
					<Button close={true} on:click={() => { fullscreenSend = false }}/>
				{/if}
				<form on:submit|preventDefault={sendQuestion}>
					<textarea
						id="question"
						bind:this={ta}
						bind:value={messageText}
						use:autoresize
						placeholder="Type your question"
						on:keypress={checkMessage}
						on:touchstart={makeFullscreen}
						on:mousedown={makeFullscreen}
					></textarea>
					{#if messageText}
						<div>
							{#if fullscreenSend && $event.setup.app.questionAnonymity}
								<p class="switch">
									<Switch ref="anon" bind:value={anon}/>
									<label for="anon">Hide my name</label>
								</p>
							{/if}
							<button type="submit" use:autoblur>
								<svg viewBox="0 0 80 80"><path d="M74.9 15.18l-.06-.29c-.07-.26-.18-.51-.31-.74l-.16-.38-.28-.21c-.18-.19-.38-.35-.6-.49l-.29-.16c-.22-.11-.44-.19-.68-.24-.13-.03-.26-.05-.34-.05-.24-.03-.48-.03-.7 0l-.26.04-.14.02-63.81 19.11c-1.26.38-2.15 1.51-2.22 2.82-.07 1.31.7 2.53 1.82 3.01l21.96 10.49-.45 15.95-.01.23.01.09c.01.19.03.37.06.54l.09.35c.07.21.17.42.3.63l.15.24.15.22.16.13.27.24.51.34.1.03c.14.06.29.12.43.16l.26.07c.22.04.42.06.63.06h.14l.26-.02c.75-.08 1.43-.44 1.95-1.01l11.42-10.4 13.23 6.32c.44.22.92.34 1.41.34l.27-.01c.34-.03.67-.11.97-.25.88-.38 1.52-1.12 1.77-2.05l11.92-43.79.07-.31.02-.19v-.01l.02-.3c.01-.21-.01-.4-.04-.53zm-9.93 3.5l-34.3 25.88-20.07-9.59 54.37-16.29zm-32.54 43.58l.34-12.26 8.6 4.11-8.94 8.15zm26.91-4l-24.6-11.75 34.98-26.4-10.38 38.15z"/></svg>
							</button>
						</div>
						{#if fullscreenSend}
							<span class="counter" class:over={remaining < 0}>{remaining}</span>
						{/if}
					{/if}
				</form>
			</div>
		{/if}
		
	</div>

	{#if !fullscreenSend && !$showSettings}
		<div class="new" in:fade={{ duration: 400, delay: 400 }} out:fade|local={{ duration: 200 }}>
			<Button on:click={makeFullscreen}>
				<svg viewBox="0 0 80 80"><path d="m31.96 59.32-12.66 3.33c-.55.15-1.14-.01-1.54-.42-.4-.4-.56-.99-.42-1.54l3.34-12.65z"/><path d="m49.05 19.64c3.12-3.12 8.19-3.12 11.31 0s3.12 8.19 0 11.31z"/><path d="m57.48 33.84-11.31-11.32-22.63 22.63 4.41 4.4 19.81-19.81 2.45 2.45-19.81 19.81 4.46 4.46z"/></svg>
			</Button>
		</div>
	{/if}
{/if}